import type {AjaxSuccessResponse} from '@Utils/ajax.util';
import type {
  AiGeneratedContent,
  EmailCampaignAIContentPromptOptions,
  EmailSubjectLineContentResponse,
  MultiContentAiGeneration,
  PromptBoxAlterationLoadingText,
  PromptBoxTitles,
  RecentlyGeneratedAiContent,
  RewriteResponse,
  SelectedValueType,
} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {PromptBoxConfigOptionType} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';

export const EMAIL_CAMPAIGN_AI_CONTENT_API_PATH = 'emailcampaignaicontent';

const requestUseEmailSubjectLine = async (
  prompt: string,
  subject: string,
  preview_text: string,
  selectedValues: EmailCampaignAIContentPromptOptions
): Promise<AjaxSuccessResponse> => {
  const {entityId, entityType} = getEmailCmpaignPublishingEntityTypeAndId();
  return (await window.PMW.writeLocal(`${EMAIL_CAMPAIGN_AI_CONTENT_API_PATH}/useEmailSubjectLine`, {
    prompt,
    subject,
    preview_text,
    configOptions: selectedValues,
    entityId,
    entityType,
  })) as AjaxSuccessResponse;
};

const requestReuseAiEmailSubjectLine = async (contentGroupKeyForEmailSubjectLine: string): Promise<AjaxSuccessResponse> => {
  return (await window.PMW.writeLocal(`${EMAIL_CAMPAIGN_AI_CONTENT_API_PATH}/reuseAiEmailSubjectLine`, {
    contentGroupKeyForEmailSubjectLine,
  })) as AjaxSuccessResponse;
};

const requestRewritePrompt = async (prompt: string, selectedValues: EmailCampaignAIContentPromptOptions, originator: GenerateIdeasOriginator): Promise<RewriteResponse> => {
  const {entityId, entityType} = getEmailCmpaignPublishingEntityTypeAndId();
  return (await window.PMW.readLocal(`${EMAIL_CAMPAIGN_AI_CONTENT_API_PATH}/rewrite`, {
    prompt,
    configOptions: selectedValues,
    originator,
    entityId,
    entityType,
  })) as RewriteResponse;
};

const requestGetRecents = async (page = 1, per_page = 10): Promise<EmailSubjectLineContentResponse[]> => {
  return (await window.PMW.readLocal(`${EMAIL_CAMPAIGN_AI_CONTENT_API_PATH}/getRecents`, {
    page,
    per_page,
  })) as EmailSubjectLineContentResponse[];
};

export const recents = async (): Promise<RecentlyGeneratedAiContent[]> => {
  const response = await requestGetRecents();
  return response.map((subject) => {
    const selectedValuesForSubject = {
      [PromptBoxConfigOptionType.EMAIL_CAMPAIGN_SUBJECT_TONE]: subject.subject_line.options.tone,
      [PromptBoxConfigOptionType.EMOJIS]: subject.subject_line.options.useEmojis,
      [PromptBoxConfigOptionType.PURPOSE]: subject.subject_line.options.purpose,
    };

    return {
      id: subject.content_group_key,
      text: subject.subject_line.content,
      subtext: subject.preview_text.content,
      configOptions: selectedValuesForSubject,
    } as RecentlyGeneratedAiContent;
  });
};

export const generate = async (prompt: string, selectedValues: SelectedValueType, originator: GenerateIdeasOriginator): Promise<AiGeneratedContent[]> => {
  const response = await requestRewritePrompt(prompt, selectedValues as unknown as EmailCampaignAIContentPromptOptions, originator);
  return response.emailSubjects.map((subject): AiGeneratedContent => {
    return {text: subject.subject, subtext: subject.previewText} as AiGeneratedContent;
  });
};

export const alter = async (prompt: string, selectedValues: SelectedValueType): Promise<AiGeneratedContent> => {
  const originator = GenerateIdeasOriginator.Alter;
  const response = await requestRewritePrompt(prompt, selectedValues as unknown as EmailCampaignAIContentPromptOptions, originator);
  const aiGeneratedContent = response.emailSubjects.map((subject): AiGeneratedContent => {
    return {text: subject.subject, subtext: subject.previewText} as AiGeneratedContent;
  });
  return aiGeneratedContent[Math.floor(Math.random() * 4)];
};

export const use = async (prompt: string, selectedValues: SelectedValueType, selectedContent: MultiContentAiGeneration): Promise<void> => {
  await requestUseEmailSubjectLine(prompt, selectedContent.text, selectedContent.subtext, selectedValues as unknown as EmailCampaignAIContentPromptOptions);
  window.PMW.gtm.trackGA4CustomEvent('EmailMaker_SubjectLine_AIWriter_Generated');
};

export const reuse = async (contentId: string): Promise<void> => {
  await requestReuseAiEmailSubjectLine(contentId);
};

export const getPromptBoxTitles = (): PromptBoxTitles => {
  return {
    main: window.i18next.t('pmwjs_ai_writer'),
    recents: window.i18next.t('pmwjs_recents'),
    selected: window.i18next.t('pmwjs_customize_selection'),
    primarySelectedContent: window.i18next.t('pmwjs_subject_line'),
    secondarySelectedContnt: window.i18next.t('pmwjs_preview_text'),
  };
};

export const getAlterationLoadingText = (): PromptBoxAlterationLoadingText => {
  return {
    primaryAlterationLoadingMessage: window.i18next.t('pmwjs_updating_subject_line'),
    secondaryAlterationLoadingMessage: window.i18next.t('pmwjs_updating_preview_text'),
  };
};

export enum GenerateIdeasOriginator {
  Generate = 'generate',
  MoreIdeas = 'more_ideas',
  Alter = 'alter',
}

export const requestResetEmailCampaignAiContentInsights = async (): Promise<void> => {
  const {entityId, entityType} = getEmailCmpaignPublishingEntityTypeAndId();
  await window.PMW.readLocal(`${EMAIL_CAMPAIGN_AI_CONTENT_API_PATH}/resetInsights`, {
    entityId,
    entityType,
  });
};

export const getEmailCmpaignPublishingEntityTypeAndId = (): {entityId: string; entityType: EmailPublishingFlow} => {
  let entityId;
  let entityType = EmailPublishingFlow.Template;

  const emailCampaignId: string | null = window.PMW.emailWizard.emailCampaignId ? (window.PMW.emailWizard.emailCampaignId as string) : null;
  const posterHashedIdForPosterCampaignFlow: string | null = window.PMW.emailWizard.hashedPosterId ? (window.PMW.emailWizard.hashedPosterId as string) : null;
  const idEmailTemplate: string | null = window.PMW.emailWizard.idEmailTemplate ? (window.PMW.emailWizard.idEmailTemplate as string) : null;

  if (emailCampaignId) {
    entityId = emailCampaignId;
    entityType = EmailPublishingFlow.Campaign;
  } else if (posterHashedIdForPosterCampaignFlow) {
    entityId = posterHashedIdForPosterCampaignFlow;
    entityType = EmailPublishingFlow.Poster;
  } else {
    entityId = idEmailTemplate;
    entityId = EmailPublishingFlow.Template;
  }

  return {entityId, entityType};
};

enum EmailPublishingFlow {
  Campaign = 'campaign',
  Template = 'template',
  Poster = 'poster',
}

export enum EmailEditorAiPromptBoxVariant {
  TEMPLATE_EDITOR = 'template_editor',
  SINGLE_TEXT_EDITOR = 'single_text_editor',
  SINGLE_TEXT_EDITOR_DROPDOWN = 'single_text_editor_dropDown',
}

export enum EmailMakerAiRewriteMode {
  REWRITE_ALL_TEXT_COMPONENTS = 'rewrite_all_text_components',
  REWRITE_SELECTED_TEXT_COMPONENT = 'rewrite_single_text_component',
}
